import { useState, useEffect } from 'react';
import noticeApi, { Notice } from '../../services/noticeApi';
import { ChevronRight, Calendar } from 'lucide-react';

// Notice Section Title
function Container15() {
  return (
    <div
      className="box-border content-stretch flex flex-col gap-10 items-center justify-start leading-[0] not-italic p-0 relative shrink-0"
      data-name="Container"
    >
      <div className="font-paperlogy font-extrabold relative shrink-0 text-center text-neutral-950 text-[min(48px,_8vw)] tracking-[-0.96px]">
        <p className="block leading-[normal] font-bold">공지사항</p>
      </div>
    </div>
  );
}

// Notice List Item
function NoticeItem({ 
  notice,
  onClick 
}: { 
  notice: Notice;
  onClick: () => void;
}) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    }).replace(/\. /g, '.').replace(/\.$/, '');
  };

  // 카테고리별 색상 및 스타일
  const getCategoryStyle = (category: string, isPinned: boolean) => {
    if (isPinned) {
      return 'bg-[#ff6b6b] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
    }
    switch(category) {
      case '공지':
        return 'bg-[#ff6b6b] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
      case '이벤트':
        return 'bg-[#ff9f40] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
      case '운영':
        return 'bg-[#6c757d] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
      case '신규콘텐츠':
        return 'bg-[#4CAF50] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
      default:
        return 'bg-[#6c757d] text-white text-[11px] px-2 py-0.5 rounded-sm font-medium';
    }
  };

  const categoryLabel = notice.is_pinned ? '중요' : 
    (notice.category === '신규콘텐츠' ? '콘텐츠' : notice.category);

  return (
    <div 
      className="bg-[#ffffff] border-b border-[#e5e5e5] box-border content-stretch flex items-center justify-between px-6 py-5 relative shrink-0 w-full hover:bg-[#fafafa] cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* 카테고리 태그 */}
        <span className={getCategoryStyle(notice.category, notice.is_pinned)}>
          {categoryLabel}
        </span>
        
        {/* 제목 */}
        <div className="font-paperlogy font-normal leading-[normal] relative text-[16px] text-left text-[#333333] flex-1">
          <p className="block truncate">{notice.title}</p>
        </div>
      </div>

      {/* 날짜와 화살표 */}
      <div className="flex items-center gap-4">
        <div className="font-paperlogy font-normal leading-[normal] text-[14px] text-[#999999] text-right">
          {formatDate(notice.created_at)}
        </div>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 12L10 8L6 4" stroke="#CCCCCC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
}

// Notice List Container with API
function Container16({ onNoticeClick, onNoticePageClick }: { 
  onNoticeClick: (notice: Notice) => void;
  onNoticePageClick: () => void;
}) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await noticeApi.getAll();
        // 최상단 5개만 가져오기 (이미 서버에서 고정 우선, 최신순 정렬됨)
        setNotices(data.slice(0, 5));
      } catch (err) {
        console.error('공지사항 불러오기 실패:', err);
        setError('공지사항을 불러올 수 없습니다.');
        // 에러 시 빈 배열로 설정
        setNotices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  if (loading) {
    return (
      <div className="box-border content-stretch flex flex-col items-start justify-start p-0 relative shrink-0 w-full">
        <div className="bg-white border border-[#999999] rounded-2xl overflow-hidden w-full">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="box-border content-stretch flex flex-col items-start justify-start p-0 relative shrink-0 w-full">
        <div className="bg-white border border-[#999999] rounded-2xl overflow-hidden w-full">
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-gray-500 mb-4">{error}</p>
            <button 
              onClick={onNoticePageClick}
              className="text-blue-600 hover:underline"
            >
              공지사항 페이지로 이동
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (notices.length === 0) {
    return (
      <div className="box-border content-stretch flex flex-col items-start justify-start p-0 relative shrink-0 w-full">
        <div className="bg-white border border-[#999999] rounded-2xl overflow-hidden w-full">
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-gray-500">등록된 공지사항이 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="box-border content-stretch flex flex-col items-start justify-start p-0 relative shrink-0 w-full"
      data-name="Container"
    >
      <div className="bg-white border border-[#999999] rounded-2xl overflow-hidden w-full">
        {notices.map((notice) => (
          <NoticeItem 
            key={notice.id}
            notice={notice}
            onClick={() => onNoticeClick(notice)}
          />
        ))}
        
        {/* 더보기 버튼 */}
        <div 
          className="bg-gray-50 px-6 py-4 text-center cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={onNoticePageClick}
        >
          <span className="text-gray-700 font-medium">
            공지사항 더보기 →
          </span>
        </div>
      </div>
    </div>
  );
}

// Notice Section
function Section5({ onNoticeClick, onNoticePageClick }: { 
  onNoticeClick: (notice: Notice) => void; 
  onNoticePageClick: () => void 
}) {
  return (
    <div
      className="box-border content-stretch flex flex-col items-center justify-start pb-16 pt-24 px-4 md:px-6 lg:px-8 relative shrink-0 w-full bg-[#f9f9f9]"
      data-name="Section"
    >
      <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-10">
        <Container15 />
        <Container16 
          onNoticeClick={onNoticeClick} 
          onNoticePageClick={onNoticePageClick}
        />
      </div>
    </div>
  );
}

// Notice Detail Modal
function NoticeModal({ 
  notice, 
  onClose 
}: { 
  notice: Notice;
  onClose: () => void;
}) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      onClick={onClose}
    >
      <div 
        className="rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 shadow-2xl border border-gray-300"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          backgroundColor: '#FFFFFF',
          position: 'relative',
          zIndex: 51
        }}
      >
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            {notice.is_pinned && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded font-bold">
                중요
              </span>
            )}
            {notice.category && (
              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded">
                {notice.category}
              </span>
            )}
          </div>
          <h2 className="text-2xl font-bold mb-2">{notice.title}</h2>
          <div className="text-sm text-gray-600 flex items-center gap-4">
            <span>작성자: {notice.author}</span>
            <span>{formatDate(notice.created_at)}</span>
            <span>조회수: {notice.view_count}</span>
          </div>
        </div>
        
        <div className="border-t pt-4">
          <p className="whitespace-pre-wrap text-gray-700">{notice.content}</p>
          {notice.image_url && (
            <div className="mt-4">
              <img
                src={notice.image_url.startsWith('http') ? notice.image_url : `http://localhost:3001${notice.image_url}`}
                alt="첨부 이미지"
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

export function NoticeSectionWithAPI({ onNoticePageClick }: { onNoticePageClick: () => void }) {
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  
  // 공지사항 클릭 시 상세보기 (조회수 증가)
  const handleNoticeClick = async (notice: Notice) => {
    try {
      // API 호출로 조회수 증가 및 상세 정보 가져오기
      const detailedNotice = await noticeApi.getOne(notice.id);
      setSelectedNotice(detailedNotice);
    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
      // 에러 시에도 기본 정보 표시
      setSelectedNotice(notice);
    }
  };
  
  return (
    <>
      <Section5 
        onNoticeClick={handleNoticeClick} 
        onNoticePageClick={onNoticePageClick}
      />
      {selectedNotice && (
        <NoticeModal 
          notice={selectedNotice} 
          onClose={() => setSelectedNotice(null)}
        />
      )}
    </>
  );
}